/**
 * IL TESTO DELLE PAGINE DI AIUTO, IMPAGINATO.
 *
 * Il backoffice scrive testo semplice (Moduli › Aiuto, Piè di pagina ›
 * Informazioni): niente HTML, niente editor. Perche' un regolamento di venti
 * sezioni non sia un muro di righe, poche regole di scrittura che chi scrive
 * vede gia' nel testo:
 *
 *   «1. DEPOSITI»          riga numerata tutta in maiuscolo: titolo di sezione.
 *                          Con tre sezioni o piu', in cima c'e' l'indice.
 *   «IL MIO CONTO»         riga tutta in maiuscolo: sottotitolo.
 *   «• testo» o «- testo»  elenco puntato.
 *   «1) testo»             elenco numerato.
 *   «💬 TITOLO»            riga che comincia con un'emoji: riquadro in
 *                          evidenza, con dentro le righe fino alla riga vuota.
 *   «Come prelevo?»        riga da sola che finisce con «?»: domanda in grassetto.
 *
 * Il resto e' com'era: le righe vicine stanno nello stesso paragrafo e gli a
 * capo restano a capo; una riga vuota apre un paragrafo nuovo. Un testo che
 * non usa queste regole si vede come prima.
 *
 * Restituisce dati, non HTML: li disegna PaginaTesto con React, quindi
 * quello che scrive il backoffice non diventa mai codice nella pagina.
 */

const NUMERATO = /^(\d{1,2})\.\s+(.+)$/;
const PUNTO = /^[•\-–]\s+(.+)$/;
const ELENCO_NUMERATO = /^(\d{1,2})\)\s+(.+)$/;
/* ©, ® e ™ per Unicode sono emoji, ma «© 2026 …» e' una riga di testo. */
const SIMBOLI = ['©', '®', '™'];
const EMOJI = /^(\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic})*)\s*(.*)$/u;

/** Tutta in maiuscolo: almeno due lettere e nessuna minuscola. */
function maiuscola(riga) {
  const lettere = riga.replace(/[^\p{L}]/gu, '');
  return lettere.length >= 2 && lettere === lettere.toUpperCase() && lettere !== lettere.toLowerCase();
}

/** «DEPOSITI E PRELIEVI» → «Depositi e prelievi»: il titolo si legge meglio cosi'. */
function frase(riga) {
  const minuscola = riga.toLocaleLowerCase('it');
  return minuscola.charAt(0).toLocaleUpperCase('it') + minuscola.slice(1);
}

/**
 * @param {string} testo
 * @returns {{blocchi: Array<object>, indice: Array<{id: string, numero: string, titolo: string}>}}
 *   blocchi: {tipo: 'sezione', id, numero, titolo} | {tipo: 'sottotitolo', testo}
 *   | {tipo: 'paragrafo', testo} | {tipo: 'domanda', testo}
 *   | {tipo: 'punti' | 'numeri', voci: string[]}
 *   | {tipo: 'riquadro', emoji, titolo, righe: string[]}
 */
export function impagina(testo) {
  const blocchi = [];
  const indice = [];
  let paragrafo = null;
  let elenco = null;
  let riquadro = null;

  const chiudi = () => {
    paragrafo = null;
    elenco = null;
  };

  for (const grezza of String(testo || '').split(/\r?\n/)) {
    const riga = grezza.trim();
    if (!riga) {
      chiudi();
      riquadro = null;
      continue;
    }
    if (riquadro) {
      riquadro.righe.push(riga);
      continue;
    }
    const numerato = riga.match(NUMERATO);
    if (numerato && maiuscola(numerato[2])) {
      chiudi();
      const sezione = {tipo: 'sezione', id: 'sezione-' + numerato[1], numero: numerato[1], titolo: frase(numerato[2])};
      blocchi.push(sezione);
      indice.push({id: sezione.id, numero: sezione.numero, titolo: sezione.titolo});
      continue;
    }
    const emoji = riga.match(EMOJI);
    if (emoji && emoji[2] && !SIMBOLI.includes(emoji[1])) {
      chiudi();
      riquadro = {tipo: 'riquadro', emoji: emoji[1], titolo: maiuscola(emoji[2]) ? frase(emoji[2]) : emoji[2], righe: []};
      blocchi.push(riquadro);
      continue;
    }
    if (maiuscola(riga)) {
      chiudi();
      blocchi.push({tipo: 'sottotitolo', testo: frase(riga)});
      continue;
    }
    const punto = riga.match(PUNTO);
    const numero = riga.match(ELENCO_NUMERATO);
    if (punto || numero) {
      const tipo = punto ? 'punti' : 'numeri';
      if (!elenco || elenco.tipo !== tipo) {
        paragrafo = null;
        elenco = {tipo, voci: []};
        blocchi.push(elenco);
      }
      elenco.voci.push((punto || numero)[punto ? 1 : 2]);
      continue;
    }
    if (riga.endsWith('?')) {
      chiudi();
      blocchi.push({tipo: 'domanda', testo: riga});
      continue;
    }
    elenco = null;
    if (paragrafo) paragrafo.testo += '\n' + riga;
    else {
      paragrafo = {tipo: 'paragrafo', testo: riga};
      blocchi.push(paragrafo);
    }
  }
  return {blocchi, indice: indice.length >= 3 ? indice : []};
}
